import { tick } from '@boba-cli/tea';
import { Style, resolveColor, width, createDefaultContext } from '@boba-cli/chapstick';

// src/model.ts

// src/messages.ts
var FrameMsg = class {
  constructor(id, tag, time) {
    this.id = id;
    this.tag = tag;
    this.time = time;
  }
  _tag = "progress:frame";
};

// src/spring.ts
var Spring = class _Spring {
  frequency;
  damping;
  #angular;
  #pos;
  #vel;
  constructor(config = {}) {
    this.frequency = config.frequency ?? 18;
    this.damping = config.damping ?? 1;
    this.#angular = this.frequency;
    this.#pos = config.position ?? 0;
    this.#vel = config.velocity ?? 0;
  }
  /** Current position. */
  position() {
    return this.#pos;
  }
  /** Current velocity. */
  velocity() {
    return this.#vel;
  }
  /** Return a copy with new spring options, keeping state. */
  withOptions(frequency, damping) {
    return new _Spring({
      frequency,
      damping,
      position: this.#pos,
      velocity: this.#vel
    });
  }
  /**
   * Integrate toward target over the provided timestep (ms).
   * Returns the new position and velocity.
   */
  update(target, deltaMs) {
    const dt = Math.min(0.05, Math.max(0, deltaMs / 1e3));
    const displacement = this.#pos - target;
    const springForce = -this.#angular * this.#angular * displacement;
    const dampingForce = -2 * this.damping * this.#angular * this.#vel;
    const acceleration = springForce + dampingForce;
    const velocity = this.#vel + acceleration * dt;
    const position = this.#pos + velocity * dt;
    return new _Spring({
      frequency: this.frequency,
      damping: this.damping,
      position,
      velocity
    });
  }
};

// src/gradient.ts
function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}
function hexToRgb(hex) {
  const normalized = hex.startsWith("#") ? hex.slice(1) : hex;
  if (normalized.length !== 6) return null;
  const int = Number.parseInt(normalized, 16);
  if (Number.isNaN(int)) return null;
  return {
    r: int >> 16 & 255,
    g: int >> 8 & 255,
    b: int & 255
  };
}
function rgbToHex({ r, g, b }) {
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function interpolateColor(colorA, colorB, t) {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);
  if (!a || !b) return colorA;
  const tClamped = clamp01(t);
  const mix = (start, end) => Math.round(start + (end - start) * tClamped);
  return rgbToHex({
    r: mix(a.r, b.r),
    g: mix(a.g, b.g),
    b: mix(a.b, b.b)
  });
}

// src/model.ts
var FPS = 60;
var FRAME_MS = Math.round(1e3 / FPS);
var DEFAULT_WIDTH = 40;
var DEFAULT_FULL = "\u2588";
var DEFAULT_EMPTY = "\u2591";
var DEFAULT_FULL_COLOR = "#7571F9";
var DEFAULT_EMPTY_COLOR = "#606060";
var DEFAULT_PERCENT_FORMAT = " %3.0f%%";
var DEFAULT_GRADIENT_START = "#5A56E0";
var DEFAULT_GRADIENT_END = "#EE6FF8";
var SETTLE_DISTANCE = 2e-3;
var SETTLE_VELOCITY = 0.01;
var lastId = 0;
function nextId() {
  return ++lastId;
}
function clamp012(value) {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(1, value));
}
function ensureChar(input, fallback) {
  if (!input) return fallback;
  return input.slice(0, 1);
}
function formatPercent(value, fmt) {
  const percentValue = clamp012(value) * 100;
  const match = fmt.match(/%(\d+)?(?:\.(\d+))?f/);
  if (!match) {
    return `${percentValue.toFixed(0)}%`;
  }
  const precision = match[2] ? Number.parseInt(match[2], 10) : 0;
  const replacement = percentValue.toFixed(precision);
  return fmt.replace(/%(\d+)?(?:\.(\d+))?f/, replacement).replace(/%%/g, "%");
}
function settle(percent, target, velocity) {
  const dist = Math.abs(percent - target);
  return dist < SETTLE_DISTANCE && Math.abs(velocity) < SETTLE_VELOCITY;
}
function getDefaultEnv() {
  return createDefaultContext().env;
}
function resolvedColor(color, fallback) {
  return resolveColor(color, getDefaultEnv()) ?? fallback;
}
function defaultState() {
  return {
    percent: 0,
    target: 0,
    velocity: 0,
    id: nextId(),
    tag: 0,
    spring: new Spring(),
    lastFrameTime: null
  };
}
var ProgressModel = class _ProgressModel {
  width;
  full;
  empty;
  fullColor;
  emptyColor;
  showPercentage;
  percentFormat;
  gradientStart;
  gradientEnd;
  scaleGradient;
  useGradient;
  percentageStyle;
  #percent;
  #target;
  #velocity;
  #id;
  #tag;
  #spring;
  #lastFrameTime;
  constructor(options = {}, state = {}) {
    this.width = options.width ?? DEFAULT_WIDTH;
    this.full = ensureChar(options.full, DEFAULT_FULL);
    this.empty = ensureChar(options.empty, DEFAULT_EMPTY);
    this.fullColor = resolvedColor(options.fullColor, DEFAULT_FULL_COLOR);
    this.emptyColor = resolvedColor(options.emptyColor, DEFAULT_EMPTY_COLOR);
    this.showPercentage = options.showPercentage ?? true;
    this.percentFormat = options.percentFormat ?? DEFAULT_PERCENT_FORMAT;
    this.percentageStyle = options.percentageStyle ?? new Style();
    const start = options.gradientStart ? resolveColor(options.gradientStart, getDefaultEnv()) : void 0;
    const end = options.gradientEnd ? resolveColor(options.gradientEnd, getDefaultEnv()) : void 0;
    this.gradientStart = start;
    this.gradientEnd = end;
    this.scaleGradient = options.scaleGradient ?? false;
    this.useGradient = Boolean(start && end);
    const frequency = options.springFrequency ?? state.spring?.frequency ?? 18;
    const damping = options.springDamping ?? state.spring?.damping ?? 1;
    const baseState = { ...defaultState(), ...state };
    const spring = state.spring ?? new Spring({ frequency, damping });
    this.#spring = spring.withOptions(frequency, damping);
    this.#percent = clamp012(baseState.percent);
    this.#target = clamp012(baseState.target);
    this.#velocity = baseState.velocity;
    this.#id = baseState.id;
    this.#tag = baseState.tag;
    this.#lastFrameTime = baseState.lastFrameTime;
  }
  /** Create a new progress bar with defaults. */
  static new(options = {}) {
    return new _ProgressModel(options);
  }
  /** Convenience constructor with default gradient. */
  static withDefaultGradient(options = {}) {
    return new _ProgressModel({
      ...options,
      gradientStart: options.gradientStart ?? DEFAULT_GRADIENT_START,
      gradientEnd: options.gradientEnd ?? DEFAULT_GRADIENT_END
    });
  }
  /** Convenience constructor with a custom gradient. */
  static withGradient(colorA, colorB, options = {}) {
    return new _ProgressModel({
      ...options,
      gradientStart: colorA,
      gradientEnd: colorB
    });
  }
  /** Convenience constructor with solid fill. */
  static withSolidFill(color, options = {}) {
    return new _ProgressModel({
      ...options,
      fullColor: color,
      gradientStart: void 0,
      gradientEnd: void 0
    });
  }
  /** Unique ID for message routing. */
  id() {
    return this.#id;
  }
  /** Current animated percent (0-1). */
  percent() {
    return clamp012(this.#percent);
  }
  /** Target percent (0-1). */
  targetPercent() {
    return clamp012(this.#target);
  }
  /** Tea init hook (no-op). */
  init() {
    return null;
  }
  /** Handle messages; consumes FrameMsg for animation. */
  update(msg) {
    if (!(msg instanceof FrameMsg)) {
      return [this, null];
    }
    if (msg.id !== this.#id || msg.tag !== this.#tag) {
      return [this, null];
    }
    const dt = this.#lastFrameTime === null ? FRAME_MS : Math.max(1, msg.time.getTime() - this.#lastFrameTime.getTime());
    const spring = this.#spring.update(this.#target, dt);
    const nextPercent = clamp012(spring.position());
    const nextVelocity = spring.velocity();
    const next = this.withState({
      percent: nextPercent,
      velocity: nextVelocity,
      tag: this.#tag,
      spring,
      lastFrameTime: msg.time
    });
    if (settle(nextPercent, this.#target, nextVelocity)) {
      return [next, null];
    }
    return [next, next.nextFrame()];
  }
  /** Set a new target percent and start animation. */
  setPercent(percent) {
    const clamped = clamp012(percent);
    if (this.isAnimating()) {
      const next2 = this.withState({ target: clamped });
      return [next2, null];
    }
    const next = this.withState({
      target: clamped,
      tag: this.#tag + 1,
      lastFrameTime: null
    });
    return [next, next.nextFrame()];
  }
  /** Returns true if animation is in progress (not yet settled). */
  isAnimating() {
    return !settle(this.#percent, this.#target, this.#velocity);
  }
  /** Increment the target percent. */
  incrPercent(delta) {
    return this.setPercent(this.#target + delta);
  }
  /** Update the spring configuration (keeps current state). */
  setSpringOptions(frequency, damping) {
    const spring = this.#spring.withOptions(frequency, damping);
    return this.withState({ spring });
  }
  /** Render the animated progress bar. */
  view() {
    return this.viewAs(this.percent());
  }
  /** Render the bar at an explicit percent (0-1). */
  viewAs(percent) {
    const pct = clamp012(percent);
    const percentText = this.showPercentage ? this.percentageStyle.render(formatPercent(pct, this.percentFormat)) : "";
    const percentWidth = this.showPercentage ? width(percentText) : 0;
    const totalBarWidth = Math.max(0, this.width - percentWidth);
    const filledWidth = Math.max(0, Math.round(totalBarWidth * pct));
    const emptyWidth = Math.max(0, totalBarWidth - filledWidth);
    const bar = `${this.useGradient ? this.renderGradient(filledWidth, totalBarWidth) : this.renderSolid(filledWidth)}${this.renderEmpty(emptyWidth)}`;
    return `${bar}${percentText}`;
  }
  renderGradient(filledWidth, totalWidth) {
    if (!this.useGradient || filledWidth <= 0 || !this.gradientStart || !this.gradientEnd) {
      return "";
    }
    const parts = [];
    const denominator = this.scaleGradient ? Math.max(1, filledWidth - 1) : Math.max(1, totalWidth - 1);
    const ctx = createDefaultContext();
    for (let i = 0; i < filledWidth; i++) {
      const t = filledWidth === 1 ? 0.5 : i / denominator;
      const color = interpolateColor(this.gradientStart, this.gradientEnd, t);
      parts.push(new Style({}, void 0, ctx).foreground(color).render(this.full));
    }
    return parts.join("");
  }
  renderSolid(filledWidth) {
    if (filledWidth <= 0) return "";
    const ctx = createDefaultContext();
    const styled = new Style({}, void 0, ctx).foreground(this.fullColor).render(this.full);
    return styled.repeat(filledWidth);
  }
  renderEmpty(emptyWidth) {
    if (emptyWidth <= 0) return "";
    const ctx = createDefaultContext();
    const styled = new Style({}, void 0, ctx).foreground(this.emptyColor).render(this.empty);
    return styled.repeat(emptyWidth);
  }
  nextFrame() {
    const id = this.#id;
    const tag = this.#tag;
    return tick(FRAME_MS, (time) => new FrameMsg(id, tag, time));
  }
  withState(state) {
    return new _ProgressModel(
      {
        width: this.width,
        full: this.full,
        empty: this.empty,
        fullColor: this.fullColor,
        emptyColor: this.emptyColor,
        showPercentage: this.showPercentage,
        percentFormat: this.percentFormat,
        gradientStart: this.gradientStart,
        gradientEnd: this.gradientEnd,
        scaleGradient: this.scaleGradient,
        springFrequency: this.#spring.frequency,
        springDamping: this.#spring.damping,
        percentageStyle: this.percentageStyle
      },
      {
        percent: this.#percent,
        target: this.#target,
        velocity: this.#velocity,
        id: this.#id,
        tag: this.#tag,
        spring: this.#spring,
        lastFrameTime: this.#lastFrameTime,
        ...state
      }
    );
  }
};

export { FrameMsg, ProgressModel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map